import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Settings,
  ChevronDown,
  ChevronRight,
  ArrowLeft,
  ArrowRight,
} from 'lucide-react';
import { enginePhaseMapping } from '../data/processFlow';

const PHASE_LABELS: Record<string, string> = {
  gathering: 'Gathering',
  tagging: 'Tagging',
  formulating: 'Formulating',
  creating: 'Creating',
  applying: 'Applying',
  publishing: 'Publishing',
};

type Engine = {
  id: string;
  name: string;
  purpose: string;
  capabilities: string[];
  status: 'core' | 'additional' | 'future';
};

const coreEngines: Engine[] = [
  { id: 'prospector', name: 'Prospector Engine', purpose: 'Discovers and captures content from various sources', capabilities: ['Content discovery', 'OCR processing', 'File ingestion', 'Notion integration', '3rd party sync'], status: 'core' },
  { id: 'knowledge', name: 'Knowledge Engine', purpose: 'Adds metadata, categorizes, and links related materials', capabilities: ['Content management', 'Auto-tagging', 'Entity extraction', 'Notion database operations', 'Search and discovery'], status: 'core' },
  { id: 'canon', name: 'Canon Engine', purpose: 'Maintains the authoritative source of truth for all content', capabilities: ['Source of truth', 'Canonical versions', 'Deprecation management', 'Historical tracking', 'Authority enforcement'], status: 'core' },
  { id: 'authoring', name: 'Authoring Engine', purpose: 'Provides tools and interfaces for content creation', capabilities: ['Rich text editing', 'Template systems', 'Content structuring', 'Metadata management', 'Draft management'], status: 'core' },
  { id: 'studio', name: 'Studio Engine', purpose: 'Professional asset creation and design tools for visual and structural elements', capabilities: ['Icon customization', 'Table design', 'Image editing', 'Layout design', 'Color palette generation', 'Asset optimization'], status: 'core' },
  { id: 'assembly', name: 'Assembly Engine', purpose: 'Composes final deliverables from component parts', capabilities: ['Component assembly', 'Dynamic generation', 'Format adaptation', 'Personalization', 'Output optimization'], status: 'core' },
  { id: 'publishing', name: 'Publishing Engine', purpose: 'Distributes content across channels and platforms', capabilities: ['Multi-channel distribution', 'Scheduling', 'Platform adaptation', 'SEO optimization', 'Performance tracking'], status: 'core' },
  { id: 'orchestration', name: 'Orchestration Engine', purpose: 'Job Execution State Model (JESM) — manages scheduled and operator-triggered jobs with full lifecycle control', capabilities: ['Job execution state management', 'FSM-based state tracking', 'Reprocessing vs retry semantics', 'Run history and diff', 'Operator controls', 'Audit trails'], status: 'core' },
];

const additionalEngines: Engine[] = [
  { id: 'media', name: 'Media Engine', purpose: 'Manages all media assets and transformations across formats', capabilities: ['Image processing', 'Video encoding', 'Asset optimization', 'CDN distribution', 'Format conversion'], status: 'additional' },
  { id: 'education', name: 'Education Engine', purpose: 'Delivers learning experiences and tracks progress', capabilities: ['Course delivery', 'Progress tracking', 'Assessment management', 'Certification', 'Learning analytics'], status: 'additional' },
  { id: 'analytics', name: 'Analytics Engine', purpose: 'Collects data, generates insights, and drives improvement', capabilities: ['Data collection', 'Metrics tracking', 'Insight generation', 'A/B testing', 'Recommendation engines'], status: 'additional' },
  { id: 'identity', name: 'Identity & Access Engine', purpose: 'Manages authentication, authorization, and permissions', capabilities: ['User authentication', 'Role-based access', 'Permission management', 'Audit logging', 'Security enforcement'], status: 'additional' },
];

const futureEngines: Engine[] = [
  { id: 'curation', name: 'Curation & Collaboration', purpose: 'Enables team collaboration and content curation workflows', capabilities: ['Collaborative editing', 'Review workflows', 'Annotation tools', 'Feedback loops', 'Team coordination'], status: 'future' },
  { id: 'virtual-studio', name: 'Virtual Studio', purpose: 'Creates and manages virtual and interactive experiences', capabilities: ['Virtual environments', 'Interactive simulations', 'Real-time collaboration', 'Multimedia integration', 'Experience orchestration'], status: 'future' },
  { id: 'influence', name: 'Influence Engine', purpose: 'Amplifies reach and measures impact across networks', capabilities: ['Social distribution', 'Influencer management', 'Viral mechanics', 'Impact measurement', 'Network analysis'], status: 'future' },
  { id: 'simulation', name: 'Simulation', purpose: 'Models scenarios and predicts outcomes', capabilities: ['Scenario modeling', 'What-if analysis', 'Prediction engines', 'Risk assessment', 'Optimization algorithms'], status: 'future' },
];

const mofSupport = [
  { level: 'M3', label: 'Meta-Model', color: 'text-primary-700', bar: 'bg-primary-400', description: 'Knowledge Engine, Canon, and Orchestration maintain the Meta-Stack grammar and ensure consistency.' },
  { level: 'M2', label: 'Models', color: 'text-primary-700', bar: 'bg-primary-400', description: 'Authoring, Curation, and Knowledge Engine enable creation and management of registry entries.' },
  { level: 'M1', label: 'Templates', color: 'text-primary-700', bar: 'bg-primary-400', description: 'Assembly, Authoring, and Media Engine transform models into ready-to-deploy templates.' },
  { level: 'M0', label: 'Instances', color: 'text-[var(--mustard)]', bar: 'bg-[var(--mustard)]', description: 'Publishing, Education, Virtual Studio, and Influence deliver actual experiences to end users.' },
];

function EngineRow({ engine, expanded, onToggle }: { engine: Engine; expanded: boolean; onToggle: () => void }) {
  const phases = enginePhaseMapping[engine.name] ?? [];
  return (
    <div className={`border-b border-charcoal-100 last:border-0 ${engine.status === 'future' ? 'opacity-60' : ''}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 px-6 py-3 hover:bg-charcoal-50 transition-colors text-left group"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Settings className="h-4 w-4 text-charcoal-400 group-hover:text-primary-600 flex-shrink-0 transition-colors" />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-charcoal-900 group-hover:text-primary-700">{engine.name}</span>
              {engine.status === 'future' && (
                <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-charcoal-100 text-charcoal-500 border border-charcoal-200">Planned</span>
              )}
              {engine.status === 'additional' && (
                <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-charcoal-50 text-charcoal-500 border border-charcoal-200">Supporting</span>
              )}
            </div>
            <p className="text-xs text-charcoal-500 mt-0.5 leading-snug">{engine.purpose}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {phases.length > 0 && (
            <div className="hidden sm:flex items-center gap-1">
              {phases.map(p => (
                <span key={p} className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary-50 text-primary-700 border border-primary-200">
                  {PHASE_LABELS[p] ?? p}
                </span>
              ))}
            </div>
          )}
          {expanded
            ? <ChevronDown className="h-4 w-4 text-charcoal-400" />
            : <ChevronRight className="h-4 w-4 text-charcoal-400 group-hover:text-primary-600" />}
        </div>
      </button>

      {expanded && (
        <div className="px-6 pb-4 bg-charcoal-50/50 border-t border-charcoal-100">
          <div className="py-3">
            <p className="text-xs font-mono uppercase tracking-wider text-charcoal-400 mb-2">Capabilities</p>
            <div className="flex flex-wrap gap-1">
              {engine.capabilities.map(c => (
                <span key={c} className="text-xs px-2 py-0.5 rounded bg-[var(--bg-raised)] border border-charcoal-200 text-charcoal-600">{c}</span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export const Engines: React.FC = () => {
  const [expandedEngines, setExpandedEngines] = useState<Set<string>>(new Set());

  const toggleEngine = (id: string) => {
    setExpandedEngines(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-6">

      {/* ── Header ──────────────────────────────────────────────── */}
      <div>
        <Link to="/elevation/home" className="inline-flex items-center gap-2 text-sm text-charcoal-500 hover:text-charcoal-700 mb-4 group">
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
          Back to Overview
        </Link>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 bg-primary-50 flex items-center justify-center">
            <Settings className="h-4 w-4 text-primary-600" />
          </div>
          <div>
            <p className="text-xs font-mono uppercase tracking-wider text-charcoal-400">Infrastructure</p>
            <h1 className="text-2xl font-heading font-semibold text-charcoal-900">Platform Engines</h1>
          </div>
        </div>
        <p className="text-sm text-charcoal-600 max-w-3xl leading-relaxed">
          Platform engines orchestrate the creation, transformation, storage, and delivery of knowledge
          objects across the entire knowledge architecture.
        </p>
      </div>

      {/* ── Core engines ─────────────────────────────────────────── */}
      <div className="bg-[var(--bg-raised)] border border-charcoal-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-charcoal-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-mono uppercase tracking-wider text-charcoal-400 mb-0.5">Active</p>
              <h2 className="text-base font-heading font-semibold text-charcoal-900">{coreEngines.length} Core Platform Engines</h2>
              <p className="text-xs text-charcoal-500 mt-0.5">Supporting the M3→M2→M1→M0 flow. Click any engine to expand.</p>
            </div>
            <span className="text-xs font-mono text-charcoal-400">{expandedEngines.size} expanded</span>
          </div>
        </div>
        {coreEngines.map(e => (
          <EngineRow key={e.id} engine={e} expanded={expandedEngines.has(e.id)} onToggle={() => toggleEngine(e.id)} />
        ))}
      </div>

      {/* ── Additional engines ───────────────────────────────────── */}
      <div className="bg-[var(--bg-raised)] border border-charcoal-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-charcoal-100">
          <p className="text-xs font-mono uppercase tracking-wider text-charcoal-400 mb-0.5">Supporting</p>
          <h2 className="text-base font-heading font-semibold text-charcoal-900">Additional Engines</h2>
          <p className="text-xs text-charcoal-500 mt-0.5">Specialized capabilities with role in process flow still being defined.</p>
        </div>
        {additionalEngines.map(e => (
          <EngineRow key={e.id} engine={e} expanded={expandedEngines.has(e.id)} onToggle={() => toggleEngine(e.id)} />
        ))}
      </div>

      {/* ── Future engines ───────────────────────────────────────── */}
      <div className="bg-[var(--bg-raised)] border border-charcoal-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-charcoal-100">
          <p className="text-xs font-mono uppercase tracking-wider text-charcoal-400 mb-0.5">Planned</p>
          <h2 className="text-base font-heading font-semibold text-charcoal-900">Future Engines</h2>
          <p className="text-xs text-charcoal-500 mt-0.5">Planned additions that will expand the platform's capabilities.</p>
        </div>
        {futureEngines.map(e => (
          <EngineRow key={e.id} engine={e} expanded={expandedEngines.has(e.id)} onToggle={() => toggleEngine(e.id)} />
        ))}
      </div>

      {/* ── MOF support ──────────────────────────────────────────── */}
      <div className="bg-[var(--bg-raised)] border border-charcoal-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-charcoal-100">
          <p className="text-xs font-mono uppercase tracking-wider text-charcoal-400 mb-0.5">Architecture</p>
          <h2 className="text-base font-heading font-semibold text-charcoal-900">How Engines Support the MOF Stack</h2>
        </div>
        <div className="divide-y divide-charcoal-100">
          {mofSupport.map(({ level, label, bar, description }) => (
            <div key={level} className="flex items-start gap-4 px-6 py-4">
              <div className="flex items-center gap-2 flex-shrink-0 w-20">
                <div className={`w-1 h-10 rounded-full ${bar}`} />
                <div>
                  <span className="text-xs font-mono font-semibold text-charcoal-900">{level}</span>
                  <div className="text-[10px] text-charcoal-500">{label}</div>
                </div>
              </div>
              <p className="text-sm text-charcoal-600 leading-relaxed pt-0.5">{description}</p>
            </div>
          ))}
        </div>
        <div className="px-6 py-3 border-t border-charcoal-100 bg-charcoal-50/50">
          <div className="flex items-start gap-2">
            <div className="w-1 h-10 rounded-full bg-[var(--mustard)] flex-shrink-0" />
            <div className="pt-0.5">
              <span className="text-xs font-mono font-semibold text-charcoal-900">Feedback Loop</span>
              <p className="text-xs text-charcoal-500 mt-0.5">Analytics & Feedback collects data from M0 instances and drives improvements across all layers.</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Process flow link ────────────────────────────────────── */}
      <div className="flex items-center justify-between border border-charcoal-200 bg-[var(--bg-raised)] px-6 py-4">
        <div>
          <p className="text-sm font-heading font-semibold text-charcoal-900">See How Engines Work Together</p>
          <p className="text-xs text-charcoal-500 mt-0.5">Complete process flow showing how engines coordinate to transform ideas into products.</p>
        </div>
        <Link
          to="/elevation/app"
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors flex-shrink-0"
        >
          View Process Flow
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

    </div>
  );
};
